import { useEffect, useState } from "react";
import { Plus, Users, Pencil, ExternalLink, Mail } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loading, EmptyState } from "@/components/States";
import { DatePicker } from "@/components/DatePicker";
import { JobMeta, STATUS_STYLE } from "@/pages/Jobs";
import { api, fetchSubjects, formatError } from "@/lib/api";

const EMPTY = { title: "", company: "", location: "", work_mode: "onsite", experience: "", description: "", deadline: "", required_courses: [], required_subjects: [], open: true };

function Chips({ items, selected, onToggle, testPrefix }) {
  return (
    <div className="flex flex-wrap gap-2">
      {items.map(([id, label]) => (
        <button key={id} type="button" data-testid={`${testPrefix}-${id}`} onClick={() => onToggle(id)}
          className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${selected.includes(id) ? "border-aqua bg-aqua-soft text-aqua" : "border-line text-ink-2 hover:border-ink-3"}`}>{label}</button>
      ))}
    </div>
  );
}

export function JobEditor({ job, courses, subjects, onSaved, onCancel, base = "/admin/jobs", employer = false }) {
  const [f, setF] = useState(job ? { ...EMPTY, ...job, deadline: job.deadline || "" } : EMPTY);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const toggle = (k) => (id) => setF({ ...f, [k]: f[k].includes(id) ? f[k].filter((x) => x !== id) : [...f[k], id] });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    const body = { ...Object.fromEntries(Object.keys(EMPTY).map((k) => [k, f[k]])), deadline: f.deadline || null };
    try {
      if (job) await api.put(`${base}/${job.id}`, body); else await api.post(base, body);
      toast.success(employer ? "Submitted for IDDCR review" : job ? "Job updated" : "Job posted"); onSaved();
    } catch (e) { toast.error(formatError(e)); }
    setBusy(false);
  };
  return (
    <div data-testid="job-editor" className="flex flex-col gap-3 rounded-2xl border border-line bg-surface p-6">
      <h3 className="font-bold text-ink">{job ? "Edit job" : "Post a new job"}</h3>
      {employer && <p data-testid="job-review-note" className="-mt-2 text-xs text-ink-3">{job ? "Saving changes sends this job back to IDDCR for review; it goes offline until re-approved." : "IDDCR reviews every job before it goes live to learners."}</p>}
      <div className="grid gap-3 md:grid-cols-2">
        <Input data-testid="job-title-input" placeholder="Job title" value={f.title} onChange={set("title")} />
        {!employer && !job?.employer_id && <Input data-testid="job-company-input" placeholder="Company" value={f.company} onChange={set("company")} />}
        <Input data-testid="job-location-input" placeholder="Location" value={f.location} onChange={set("location")} />
        <Select value={f.work_mode} onValueChange={(v) => setF({ ...f, work_mode: v })}>
          <SelectTrigger data-testid="job-mode-select"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="onsite">On-site</SelectItem><SelectItem value="hybrid">Hybrid</SelectItem><SelectItem value="remote">Remote</SelectItem></SelectContent>
        </Select>
        <Input data-testid="job-experience-input" placeholder="Experience (e.g. 0-2 years)" value={f.experience} onChange={set("experience")} />
        <DatePicker testId="job-deadline-input" value={f.deadline} onChange={(v) => setF({ ...f, deadline: v })} placeholder="Application deadline (optional)" fromToday />
      </div>
      <Textarea data-testid="job-description-input" rows={5} maxLength={5000} placeholder="Role description, responsibilities, how selection works..." value={f.description} onChange={set("description")} />
      <p className="text-xs font-medium text-ink-2">Required certificates (learners need any one; leave empty for open to all)</p>
      <Chips testPrefix="job-req-course" items={courses.map((c) => [c.id, c.title])} selected={f.required_courses} onToggle={toggle("required_courses")} />
      <Chips testPrefix="job-req-subject" items={subjects.map((s) => [s.id, `${s.name} Mastery`])} selected={f.required_subjects} onToggle={toggle("required_subjects")} />
      <label className="flex items-center gap-2 text-sm text-ink-2"><Switch data-testid="job-open-toggle" checked={f.open} onCheckedChange={(v) => setF({ ...f, open: v })} />Open for applications</label>
      <div className="flex gap-2">
        <Button data-testid="job-save-btn" disabled={busy} onClick={save} className="rounded-full bg-solid">{employer ? "Submit for review" : job ? "Save changes" : "Post job"}</Button>
        <Button variant="ghost" data-testid="job-cancel-btn" onClick={onCancel} className="rounded-full">Cancel</Button>
      </div>
    </div>
  );
}

export function Applicants({ job, base = "/admin", statuses = ["new", "shortlisted", "rejected", "hired"] }) {
  const [items, setItems] = useState(null);
  const load = () => api.get(`${base}/jobs/${job.id}/applications`).then((r) => setItems(r.data));
  useEffect(() => { load(); }, [job.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const [busyId, setBusyId] = useState(null);
  const setStatus = async (id, status) => {
    setBusyId(id);
    try { await api.patch(`${base}/applications/${id}`, { status }); toast.success(`Marked ${status}; learner is being notified by email`); load(); } catch (e) { toast.error(formatError(e)); }
    setBusyId(null);
  };
  if (!items) return <Loading />;
  if (!items.length) return <p data-testid={`applicants-empty-${job.id}`} className="py-3 text-sm text-ink-3">No applicants yet.</p>;
  return (
    <div data-testid={`applicants-${job.id}`} className="mt-3 flex flex-col gap-2">
      {items.map((a) => (
        <div key={a.id} data-testid={`applicant-${a.id}`} className="rounded-lg border border-line bg-canvas-2/60 p-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="font-semibold text-ink">{a.name}{a.email && <span className="font-normal text-ink-3"> · {a.email}</span>}</p>
              <p className="text-xs text-ink-3">Applied {new Date(a.created_at).toLocaleString()}{a.email_status && ` · last email ${a.email_status}`}</p>
            </div>
            <div className="flex items-center gap-2">
              {a.status === "withdrawn" ? <span data-testid={`applicant-withdrawn-${a.id}`} className={`rounded-full px-2.5 py-1 text-xs font-medium ${STATUS_STYLE.withdrawn}`}>Withdrawn by learner</span> : (
              <Select value={a.status} disabled={busyId === a.id} onValueChange={(v) => setStatus(a.id, v)}>
                <SelectTrigger data-testid={`applicant-status-${a.id}`} className={`h-8 w-36 border-0 font-medium capitalize ${STATUS_STYLE[a.status]}`}><SelectValue /></SelectTrigger>
                <SelectContent>{(statuses.includes(a.status) ? statuses : [a.status, ...statuses]).map((s) => <SelectItem key={s} value={s} disabled={!statuses.includes(s)} className="capitalize">{s}</SelectItem>)}</SelectContent>
              </Select>)}
            </div>
          </div>
          {a.cover_note && <p className="mt-2 whitespace-pre-wrap text-sm text-ink-2">{a.cover_note}</p>}
          {a.status === "withdrawn" && a.withdraw_reason && <p className="mt-2 text-xs text-ink-3">Withdrawal reason: {a.withdraw_reason}</p>}
          <div className="mt-2 flex flex-wrap gap-2">
            {a.certificates.map((c) => <a key={c.id} href={c.verify_url} target="_blank" rel="noreferrer" className="rounded-full bg-surface px-2.5 py-0.5 text-xs text-aqua ring-1 ring-aqua/30 hover:underline">{c.title}</a>)}
            {a.showcase_url && <a href={a.showcase_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-medium text-ink hover:underline"><ExternalLink className="h-3 w-3" />Showcase</a>}
          </div>
        </div>
      ))}
    </div>
  );
}

export const REVIEW_STYLE = { pending: "bg-amber-100 text-amber-800", approved: "bg-emerald-100 text-emerald-800", rejected: "bg-red-100 text-red-700" };
export const REVIEW_LABEL = { pending: "Pending review", approved: "Approved", rejected: "Rejected" };

function ReviewControls({ j, onDone }) {
  const [reason, setReason] = useState("");
  const [rejecting, setRejecting] = useState(false);
  const [busy, setBusy] = useState(false);
  const review = async (decision) => {
    setBusy(true);
    try { await api.post(`/admin/jobs/${j.id}/review`, { decision, reason }); toast.success(decision === "approved" ? "Job approved and live" : "Job rejected"); onDone(); } catch (e) { toast.error(formatError(e)); }
    setBusy(false);
  };
  return (
    <div data-testid={`admin-job-review-${j.id}`} className="mt-3 flex flex-wrap items-center gap-2 rounded-lg border border-amber-200 bg-amber-50/70 p-3">
      <span className="text-xs font-medium text-amber-900">Submitted by {j.company} · awaiting your review</span>
      {rejecting ? (
        <>
          <Input data-testid={`admin-job-reject-reason-${j.id}`} value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} placeholder="Reason shared with the employer" className="h-8 min-w-60 flex-1 bg-surface" />
          <Button size="sm" data-testid={`admin-job-reject-confirm-${j.id}`} disabled={busy || !reason.trim()} onClick={() => review("rejected")} className="rounded-full bg-red-600 hover:bg-red-700">Reject</Button>
          <Button size="sm" variant="ghost" onClick={() => setRejecting(false)} className="rounded-full">Cancel</Button>
        </>
      ) : (
        <div className="ml-auto flex gap-2">
          <Button size="sm" disabled={busy} data-testid={`admin-job-approve-${j.id}`} onClick={() => review("approved")} className="rounded-full bg-emerald-700 hover:bg-emerald-800">Approve</Button>
          <Button size="sm" variant="outline" data-testid={`admin-job-reject-${j.id}`} onClick={() => setRejecting(true)} className="rounded-full border-red-200 text-red-700">Reject</Button>
        </div>
      )}
    </div>
  );
}

function JobRow({ j, onEdit, onToggle, onReload }) {
  const [show, setShow] = useState(false);
  const rs = j.review_status || "approved";
  return (
    <div data-testid={`admin-job-${j.id}`} className="rounded-xl border border-line bg-surface p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="font-semibold text-ink">{j.title} {j.employer_id && <span data-testid={`admin-job-review-status-${j.id}`} className={`ml-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${REVIEW_STYLE[rs]}`}>{REVIEW_LABEL[rs]}</span>} {rs === "approved" && !j.live && <span className="ml-1 rounded-full bg-elevated px-2 py-0.5 text-[11px] font-medium text-ink-3">{j.open ? "Deadline passed" : "Closed"}</span>}</p>
          <JobMeta j={j} />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label className="flex items-center gap-2 text-xs text-ink-3"><Switch data-testid={`admin-job-open-${j.id}`} checked={j.open} onCheckedChange={(v) => onToggle(j, v)} />{j.open ? "Open" : "Closed"}</label>
          <Button size="sm" variant="outline" data-testid={`admin-job-edit-${j.id}`} onClick={() => onEdit(j)} className="rounded-full"><Pencil className="mr-1.5 h-3.5 w-3.5" />Edit</Button>
          <Button size="sm" variant="outline" data-testid={`admin-job-applicants-btn-${j.id}`} onClick={() => setShow(!show)} className="rounded-full"><Users className="mr-1.5 h-3.5 w-3.5" />{j.applicants} applicant{j.applicants === 1 ? "" : "s"}</Button>
        </div>
      </div>
      {rs === "pending" && <ReviewControls j={j} onDone={onReload} />}
      {rs === "rejected" && j.review_reason && <p className="mt-2 text-xs text-red-700">Rejected: {j.review_reason}</p>}
      {show && <Applicants job={j} />}
    </div>
  );
}

function DigestButton() {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/jobs/send-digest");
      let r = { status: "running" };
      for (let i = 0; i < 20 && r.status === "running"; i++) {
        await new Promise((res) => setTimeout(res, 1500));
        r = (await api.get(`/admin/jobs/digest-runs/${data.run_id}`)).data;
      }
      if (r.status === "done" && r.sent) toast.success(`Digest sent to ${r.sent} learner${r.sent === 1 ? "" : "s"}${r.failed ? ` (${r.failed} couldn't be emailed)` : ""}`);
      else if (r.status === "done" && r.failed) toast.error(`Couldn't email ${r.failed} learner${r.failed === 1 ? "" : "s"}. The email service may be busy; try again later.`);
      else if (r.status === "done") toast.info("No learners had new matching jobs to email");
      else if (r.status === "skipped") toast.info("A digest is already being sent. Please wait for it to finish.");
      else if (r.status === "error") toast.error("The digest run failed. Please try again.");
      else toast.info("Digest is still sending in the background");
    } catch (e) { toast.error(formatError(e)); }
    setBusy(false);
  };
  return (
    <Button variant="outline" data-testid="admin-send-digest-btn" disabled={busy} onClick={run} className="rounded-full" title="Emails weekly-digest learners about matching jobs from the last 7 days they haven't been told about">
      <Mail className="mr-1.5 h-4 w-4" />{busy ? "Sending digest..." : "Send digest now"}
    </Button>
  );
}

export default function AdminJobs() {
  const [jobs, setJobs] = useState(null);
  const [courses, setCourses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [editing, setEditing] = useState(undefined);
  const load = () => api.get("/admin/jobs").then((r) => setJobs(r.data)).catch((e) => toast.error(formatError(e)));
  useEffect(() => { load(); api.get("/admin/courses").then((r) => setCourses(r.data)); fetchSubjects().then(setSubjects); }, []);
  const toggle = async (j, open) => {
    const body = Object.fromEntries(Object.keys(EMPTY).map((k) => [k, j[k] ?? EMPTY[k]]));
    try { await api.put(`/admin/jobs/${j.id}`, { ...body, open }); toast.success(open ? "Job opened" : "Job closed"); load(); } catch (e) { toast.error(formatError(e)); }
  };
  if (!jobs) return <Loading />;
  return (
    <div className="flex flex-col gap-4">
      {editing !== undefined ? (
        <JobEditor key={editing?.id || "new"} job={editing} courses={courses} subjects={subjects} onCancel={() => setEditing(undefined)} onSaved={() => { setEditing(undefined); load(); }} />
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          <Button data-testid="admin-job-new-btn" onClick={() => setEditing(null)} className="rounded-full bg-solid"><Plus className="mr-1.5 h-4 w-4" />Post a job</Button>
          <DigestButton />
          <span className="text-xs text-ink-3">Weekly job digest goes out automatically every Monday 9:00 AM IST</span>
        </div>
      )}
      {jobs.length === 0 ? <EmptyState testId="admin-jobs-empty" title="No jobs posted yet" text="Post clinical research openings for learners with matching certificates." /> : (
        <div data-testid="admin-jobs-list" className="flex flex-col gap-3">{jobs.map((j) => <JobRow key={j.id} j={j} onEdit={setEditing} onToggle={toggle} onReload={load} />)}</div>
      )}
    </div>
  );
}
